import React, {
 useState, useEffect, useCallback, useContext
} from 'react';
import {
 useParams
} from 'react-router-dom';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from 'shared/components/ui/dialog';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 Loader2
} from 'lucide-react';
import {
 actionFlowConfigService
} from 'shared/api/services/BEX';
import {
 Context
} from 'shared/context/GlobalState';
import {
 useDataBuilderV2
} from '../DataBuilderV2Context';
import {
 WorkflowView, WorkflowListItem, WorkflowStep
} from '../types/workflow.types';
import WorkflowCards from '../components/WorkflowCards';
import WorkflowMethodSelect from '../components/WorkflowMethodSelect';
import AIGenerateWorkflow from '../components/AIGenerateWorkflow';
import WorkflowEditor from '../components/WorkflowEditor';

const WorkflowsPage: React.FC = () => {
    const {
 appId, oragID
} = useParams<{ appId: string; oragID: string }>();
    const {
 addNewNotifcation
} = useContext(Context);
    const {
 refreshStats
} = useDataBuilderV2();

    const [workflows, setWorkflows] = useState<WorkflowListItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [view, setView] = useState<WorkflowView>('list');
    const [editingWorkflow, setEditingWorkflow] = useState<WorkflowListItem | null>(null);
    const [prefillData, setPrefillData] = useState<{ name: string; description: string; steps: WorkflowStep[] } | null>(null);

    // Delete confirmation state
    const [deleteTarget, setDeleteTarget] = useState<WorkflowListItem | null>(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);

    const fetchWorkflows = useCallback(async () => {
        if (!oragID || !appId) return;
        setIsLoading(true);
        try {
            const data = await actionFlowConfigService.listActionFlowConfigs(oragID, appId);
            if (Array.isArray(data)) {
                const mapped: WorkflowListItem[] = data.map((item: any) => ({
                    id: item.action_flow_id,
                    action_flow_id: item.action_flow_id,
                    name: item.name || '',
                    description: item.description || '',
                    steps: item.steps || [],
                    createdAt: item.created_at || '',
                    updatedAt: item.updated_at || '',
                }));
                setWorkflows(mapped);
            }
        } catch (error) {
            console.error('Error fetching workflows:', error);
        } finally {
            setIsLoading(false);
        }
    }, [oragID, appId]);

    useEffect(() => {
        fetchWorkflows();
    }, [fetchWorkflows]);

    const handleSave = async (data: { name: string; description: string; steps: WorkflowStep[] }) => {
        if (!oragID || !appId) return;

        try {
            if (editingWorkflow) {
                await actionFlowConfigService.updateActionFlowConfig(
                    editingWorkflow.action_flow_id,
                    {
                        org_id: oragID,
                        app_id: appId,
                        name: data.name,
                        description: data.description,
                        steps: data.steps,
                    }
                );
                addNewNotifcation('Workflow updated successfully', 'success');
            } else {
                await actionFlowConfigService.createActionFlowConfig({
                    org_id: oragID,
                    app_id: appId,
                    name: data.name,
                    description: data.description,
                    steps: data.steps,
                });
                addNewNotifcation('Workflow created successfully', 'success');
            }

            await fetchWorkflows();
            await refreshStats();
            setView('list');
            setEditingWorkflow(null);
            setPrefillData(null);
        } catch (error: any) {
            console.error('Save failed:', error);
            addNewNotifcation(error?.message || 'Failed to save workflow', 'danger');
        }
    };

    const handleEdit = (workflow: WorkflowListItem) => {
        setEditingWorkflow(workflow);
        setPrefillData(null);
        setView('editor');
    };

    const handleDuplicate = async (workflow: WorkflowListItem) => {
        if (!oragID || !appId) return;
        try {
            await actionFlowConfigService.createActionFlowConfig({
                org_id: oragID,
                app_id: appId,
                name: `${workflow.name}_copy`,
                description: workflow.description,
                steps: workflow.steps.map((s) => ({
                    ...s,
                    id: `step_${Date.now()}_${Math.random().toString(36).slice(2)}`,
                })),
            });
            addNewNotifcation(`Workflow duplicated as "${workflow.name}_copy"`, 'success');
            await fetchWorkflows();
            await refreshStats();
        } catch (error) {
            console.error('Duplicate failed:', error);
            addNewNotifcation('Failed to duplicate workflow', 'danger');
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget || !oragID || !appId) return;
        setDeleteLoading(true);
        try {
            await actionFlowConfigService.deleteActionFlowConfig(oragID, appId, deleteTarget.action_flow_id);
            setWorkflows((prev) => prev.filter((w) => w.id !== deleteTarget.id));
            addNewNotifcation('Workflow deleted successfully', 'success');
            setDeleteTarget(null);
            setDeleteConfirmText('');
            await refreshStats();
        } catch (error) {
            console.error('Delete failed:', error);
            addNewNotifcation('Failed to delete workflow', 'danger');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleNewWorkflow = () => {
        setEditingWorkflow(null);
        setPrefillData(null);
        setView('method-select');
    };

    const handleAIResult = (data: { name: string; description: string; steps: WorkflowStep[] }) => {
        setPrefillData(data);
        setEditingWorkflow(null);
        setView('editor');
    };

    const handleEditorCancel = () => {
        setView('list');
        setEditingWorkflow(null);
        setPrefillData(null);
    };

    switch (view) {
        case 'method-select':
            return (
                <WorkflowMethodSelect
                    onSelectManual={() => setView('editor')}
                    onSelectAI={() => setView('ai-generate')}
                    onBack={() => setView('list')}
                />
            );
        case 'ai-generate':
            return (
                <AIGenerateWorkflow
                    onBack={() => setView('method-select')}
                    onResult={handleAIResult}
                />
            );
        case 'editor':
            return (
                <WorkflowEditor
                    workflow={editingWorkflow}
                    prefillData={prefillData}
                    onSave={handleSave}
                    onCancel={handleEditorCancel}
                />
            );
        default:
            return (
                <>
                    <WorkflowCards
                        workflows={workflows}
                        isLoading={isLoading}
                        onEdit={handleEdit}
                        onDuplicate={handleDuplicate}
                        onDelete={(w) => setDeleteTarget(w)}
                        onNewWorkflow={handleNewWorkflow}
                    />

                    {/* Delete confirmation dialog */}
                    <Dialog
                        open={!!deleteTarget}
                        onOpenChange={(open) => {
                            if (!open) {
                                setDeleteTarget(null);
                                setDeleteConfirmText('');
                            }
                        }}
                    >
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Delete Workflow</DialogTitle>
                                <DialogDescription>
                                    Are you sure you want to delete the <span className="font-semibold">"{deleteTarget?.name}"</span> workflow?
                                    This action cannot be undone.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="py-2">
                                <p className="text-sm text-muted-foreground mb-2">
                                    Type <span className="font-semibold">{deleteTarget?.name}</span> to confirm:
                                </p>
                                <Input
                                    placeholder="Type workflow name to confirm"
                                    value={deleteConfirmText}
                                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                                />
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="destructive"
                                    disabled={deleteConfirmText !== deleteTarget?.name || deleteLoading}
                                    onClick={handleDelete}
                                >
                                    {deleteLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Delete
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </>
            );
    }
};

export default WorkflowsPage;
